import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceItem } from '@klickdata/core/resource-item';
import { User } from '@klickdata/core/user';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import * as moment from 'moment';
import { EMPTY, filter, map, merge, Observable, of, tap } from 'rxjs';
import { buttonForPlaybackAction, ResourcePlayabilityService } from './resource-playability.service';
import { OccasionDate, PlayableResourceSpec, Resource, ResourceCheckKeys } from './resource.model';
import { StartResourceService } from './start-resource.service';
import { ResourceTypes } from './types.enum';
import { Utils } from '@klickdata/core/util';

@Injectable({
    providedIn: 'root',
})
export class ResourceActionHandlerService extends OnDestroyHandler {
    public resource: Resource;
    public resPlayableSpec: PlayableResourceSpec;
    public item: ResourceItem;
    public occasionDate: OccasionDate;
    public authUser: User;
    private readonly mobile = inject(MobileService);
    private readonly router = inject(Router);
    private readonly location = inject(Location);
    private readonly resourcePlayabilityService = inject(ResourcePlayabilityService);
    private readonly startResourceService = inject(StartResourceService);

    public playResource(params: {
        resource: Resource;
        resPlayableSpec: PlayableResourceSpec;
        occasionDate?: OccasionDate;
        item?: ResourceItem;
    }): Observable<Resource> {
        this.resource = params.resource;
        this.resPlayableSpec = params.resPlayableSpec;
        this.item = params.item;
        this.occasionDate = params.occasionDate;
        if (!params.resPlayableSpec) {
            return this.startResourceUnconditionally();
        }
        if (this.resPlayableSpec.desc) {
            return this.informNotAllowedAction();
        }
        if (this.resPlayableSpec.isAllowed && this.resPlayableSpec.checkType === ResourceCheckKeys.OPPORTUNITY) {
            return this.continueToOpportunity();
        }
        if (this.isAllowedToStartResourceUnconditionally(this.resource)) {
            return this.startResourceUnconditionally();
        }
        if (this.resource.user_enrollment?.approval_status) {
            const msgBody = this.resource.user_enrollment.admin?.message?.body;
            const msgBodyHtml = msgBody
                ? '<span class="reviewer-msg-title">' +
                  $localize`The administrator message: ` +
                  `</span><span class="reviewer-msg-body">${msgBody}</span>`
                : null;
            switch (this.resource.user_enrollment?.approval_status) {
                case 'pending':
                    return this.informEnrollmentStatus(this.resource, [
                        $localize`Your request is pending`,
                        msgBodyHtml,
                    ]);
                case 'waiting':
                    return this.informEnrollmentStatus(this.resource, [
                        $localize`Your request is waiting`,
                        msgBodyHtml,
                    ]);
                case 'rejected':
                    return this.informEnrollmentStatus(this.resource, [msgBodyHtml]);
                case 'approved':
                case 'enrolled':
                    if (
                        this.resourcePlayabilityService.canStartOccasionByDate(
                            this.resource,
                            this.getApprovedOrEnrolledOccasionDate(this.resource) ?? this.occasionDate
                        )
                    ) {
                        return this.startResourceAfterApproval(this.resource, [msgBodyHtml]);
                    }
                    return this.informEnrollmentStatus(this.resource, [this.resPlayableSpec.desc, msgBodyHtml]);
                default:
                    break;
            }
        }
        let enrolledOccId: number;
        if (!!this.resource.user_enrollment?.metadata?.occasion_id) {
            enrolledOccId = this.resource.user_enrollment?.metadata?.occasion_id;
        }
        return this.startResourceService.askForEnroll(this.resource, true, this.item, enrolledOccId);
    }

    public informNotAllowedAction(): Observable<Resource> {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.INFORM_TAKE_RESOURCE,
            data: {
                resource: this.resource,
                icon: this.resPlayableSpec.icon,
                title: this.resPlayableSpec.label,
                descs: [this.resPlayableSpec.desc, this.getCompetenceClosedDesc(this.resource)],
            },
        });
        return EMPTY;
    }
    public continueToOpportunity(resource?: Resource): Observable<Resource> {
        resource = resource ?? this.resource;
        const resourceLabel = ResourceTypes.label(resource.type_id, { capitalize: false });
        const actions = [
            {
                label: $localize`Continue`,
                value: 2,
                class: 'pos-btn',
            },
            { label: $localize`Cancel`, value: 0, class: 'null-btn' },
        ];
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.INFORM_TAKE_RESOURCE,
            data: {
                resource: resource,
                icon: 'double_arrow',
                title: $localize`Continue`,
                actions: actions,
                descs: [
                    $localize`You have started this ${resourceLabel} on ${moment(
                        resource.opportunity_started_at
                    ).format('MMM D, YYYY')}`,
                    this.getCompetenceClosedDesc(resource),
                ],
                type: ResourceCheckKeys.OPPORTUNITY,
            },
        });
        return merge(this.mobile.getSideNavAction(), this.mobile.getSideNavResponseData()).pipe(
            filter(
                (data) =>
                    SideNaveActionsTypes.isValid(data) || (data.type === ResourceCheckKeys.OPPORTUNITY && data.response)
            ),
            filter((data) => data.response == 2),
            tap(() => this.startResource(resource))
        );
    }
    private isAllowedToStartResourceUnconditionally(resource: Resource): boolean {
        return (
            !resource.price &&
            (!resource.enrollmentRequiredMetadata ||
                (resource.enrollmentRequiredMetadata.occasion_always_open && !resource.enrollmentRequiredStatus) ||
                (!resource.enrollmentRequiredMetadata.occasion_dates?.filter((occ) => !occ.disabled)?.length &&
                    !resource.enrollmentRequiredStatus))
        );
    }
    public startResourceUnconditionally(resource?: Resource, item?: ResourceItem): Observable<Resource> {
        resource = resource ?? this.resource;
        const playbackButton = buttonForPlaybackAction(resource.playbackAction);
        const actions = [
            {
                label: playbackButton.label,
                value: 2,
                class: 'pos-btn',
            },
            { label: $localize`Cancel`, value: 0, class: 'null-btn' },
        ];
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.INFORM_TAKE_RESOURCE,
            data: {
                resource: resource,
                title: playbackButton.label,
                icon: playbackButton.icon,
                actions: actions,
                descs: [this.getCompetenceClosedDesc(resource)],
                type: ResourceCheckKeys.ENROLL,
            },
        });
        return merge(this.mobile.getSideNavAction(), this.mobile.getSideNavResponseData()).pipe(
            filter(
                (data) =>
                    SideNaveActionsTypes.isValid(data) || (data.type === ResourceCheckKeys.ENROLL && data.response)
            ),
            filter((data) => data.response == 2),
            tap(() => this.startResource(resource))
        );
    }

    public informEnrollmentStatus(resource: Resource, descs: string[]): Observable<Resource> {
        resource = resource ?? this.resource;
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.INFORM_TAKE_RESOURCE,
            data: {
                resource: resource,
                icon: this.resPlayableSpec.icon,
                title: this.resPlayableSpec.label,
                descs: [...descs, this.getCompetenceClosedDesc(resource), this.getApplyingInfo(resource)],
            },
        });
        return EMPTY;
    }
    public startResourceAfterApproval(resource: Resource, descs: string[]): Observable<Resource> {
        resource = resource ?? this.resource;
        const actions = [
            {
                label: $localize`Start`,
                value: 2,
                class: 'pos-btn',
            },
            { label: $localize`Cancel`, value: 0, class: 'null-btn' },
        ];
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.INFORM_TAKE_RESOURCE,
            data: {
                resource: resource,
                icon: this.resPlayableSpec.icon,
                title: this.resPlayableSpec.label,
                actions: actions,
                descs: [...descs, this.getCompetenceClosedDesc(resource), this.getApplyingInfo(resource)],
                type: ResourceCheckKeys.ENROLL,
            },
        });
        return merge(this.mobile.getSideNavResponseData()).pipe(
            filter((data) => data.type === ResourceCheckKeys.ENROLL && data.response),
            filter((data) => data.response == 2),
            tap(() => this.startResource(resource))
        );
    }
    private getApplyingInfo(resource: Resource): string {
        let apliedOccasionDate: OccasionDate;
        if (!!resource.enrollmentRequiredMetadata?.occasion_dates?.filter((occ) => !occ.disabled)?.length) {
            apliedOccasionDate = resource.enrollmentRequiredMetadata?.occasion_dates
                ?.filter((occ) => !occ.disabled)
                ?.find(
                    (date: OccasionDate) =>
                        resource.user_enrollment?.metadata?.occasion_id &&
                        date.id === resource.user_enrollment?.metadata?.occasion_id
                );
        }
        if (!apliedOccasionDate) return '';
        const appliedAt = moment(resource.user_enrollment.requested_at).format('MMM D, YYYY h:mm a');
        const startDate = moment(apliedOccasionDate.start_date).format('MMM D, YYYY h:mm a');
        const endDate = moment(apliedOccasionDate.end_date).format('MMM D, YYYY h:mm a');
        return (
            '<div class="applying-info">' +
            '<div class="applying-date">' +
            $localize`You applied on ` +
            `<strong class="applying-date">${appliedAt}</strong>` +
            '</div>' +
            '<div class="occasion-dates">' +
            $localize` on occasion ` +
            `<strong class="occasion-dates">${startDate} - ${endDate}</strong>` +
            '</div>' +
            '</div>'
        );
    }
    public startResource(resource?: Resource, item?: ResourceItem) {
        resource = resource ?? this.resource;
        item = item ?? this.item;
        let queryParams: any = {
            referrer: this.location.path().split('?')[0],
            ...(item && { position: item.play_position }),
        };
        if (!!resource.user_enrollment?.metadata?.occasion_id) {
            queryParams = { ...queryParams, occasionDateId: resource.user_enrollment?.metadata?.occasion_id };
        }
        this.router.navigate(['/player/resource', resource.id, ResourceTypes.playerRoute(resource.type_id)], {
            queryParams,
        });
    }
    public getApprovedOrEnrolledOccasionDate(resource: Resource): OccasionDate {
        if (!resource.enrollmentMetadata?.occasion_id) return null;
        if (!resource.enrollmentRequiredMetadata?.occasion_dates?.length) return null;
        return resource.enrollmentRequiredMetadata.occasion_dates.find(
            (occ) => occ.id === resource.user_enrollment?.metadata?.occasion_id
        );
    }
    private getCompetenceClosedDesc(resource: Resource): string {
        return Utils.isResourceHasUserCompetenceRecurringExpiry(resource.user_competence);
    }
}
